import { expose, getDecimalSeparator } from '@/ts/utilities';
import { CountUp } from 'countup.js';
import { Odometer } from 'odometer_countup';
import { canAnimate } from '@/ts/utils/animations/enabler';

interface counterArgs {
    $target: HTMLElement;
    start?: number;
    timing: number;
    value?: number;
}

/**
 * This function relies on the IntersectionObserver API to activate animation when the element is visible in page
 */
const observer = new IntersectionObserver(
    (entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // The code here will run when the element becomes visible
                counter({
                    $target: entry.target as HTMLElement,
                    timing: 500,
                });
                observer.unobserve(entry.target);
            }
        });
    },
    {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
    }
);

/**
 * This function handles the animation to count up from zero to target value parsed from its contents.
 */
function counter(args: counterArgs): void {
    const { $target, start = 0, timing, value } = args;

    const decimalSeparator = getDecimalSeparator();
    const number = $target.innerText.replaceAll(/[^\d\.,]/gim, '');

    let end: number;

    if (value) {
        end = value;
    } else {
        if (decimalSeparator === '.') {
            end = parseFloat(number.replaceAll(',', ''));
        } else {
            end = parseFloat(number.replaceAll('.', '').replaceAll(',', '.'));
        }
    }

    const countUp = new CountUp($target, end, {
        plugin: new Odometer({ duration: timing / 1000, lastDigitDelay: 0 }),
        duration: timing / 1000,
    });
    countUp.start();
}

/**
 * This function wraps the animation into a single callable function to allow easy on-off switches into the website.
 */
export const animateNumber = canAnimate(($target: HTMLElement, value?: number): void => {
    $target.setAttribute('data-value', value?.toString() || '');
    observer.observe($target);
});
expose('animateNumber', animateNumber);
